import { useMemo } from "react";
import { useAuth } from "tecrmi_components/useAuth";
import { canAccess, canAdmin, canReview } from "@config/permissions";

/**
 * Resolves the current user's generation permissions from BeeHive's
 * `useAuth()`. Permissions live on `user.userApplicationAccess[].
 * effectivePermissions` (BeeHive's shape, see `module-federation.d.ts`); the
 * union across the user's application-access entries is the effective set.
 *
 * The booleans apply the D21 hierarchy (see `config/permissions.ts`): a higher
 * tier implies the lower ones, so an admin-only user passes every check.
 *
 * The fake auth hook used in standalone preview returns the same shape with a
 * single access entry whose permissions come from the chosen preview user, so
 * this hook is identical in both the shell and preview.
 */
export interface GenerationPermissions {
  /** Any generation tier — may see the module. */
  canAccess: boolean;
  /** `generation.review` or `generation.admin` — may read the queue and decide. */
  canReview: boolean;
  /** `generation.admin` — may publish and regenerate. */
  canAdmin: boolean;
  /** The raw effective permission set, for ad-hoc checks. */
  permissions: ReadonlySet<string>;
}

/**
 * The shell gates its navigation on BeeHive's `selectedOrganisation` (localStorage, written by
 * BeeHive; read by the shell's `useSelectedOrganisation`). The module runs on the shell's page, so
 * it reads the same entry and unions it with `useAuth()`, so both sides agree on what the user holds.
 */
function readSelectedOrganisationPermissions(): string[] {
  try {
    const raw = window.localStorage.getItem("selectedOrganisation");
    const parsed = raw ? (JSON.parse(raw) as { effectivePermissions?: unknown }) : null;
    return Array.isArray(parsed?.effectivePermissions)
      ? parsed.effectivePermissions.filter((p): p is string => typeof p === "string")
      : [];
  } catch {
    return [];
  }
}

export function usePermissions(): GenerationPermissions {
  const { user } = useAuth();

  return useMemo(() => {
    const perms = new Set<string>(readSelectedOrganisationPermissions());
    for (const access of user?.userApplicationAccess ?? []) {
      for (const p of access.effectivePermissions ?? []) {
        perms.add(p);
      }
    }
    return {
      canAccess: canAccess(perms),
      canReview: canReview(perms),
      canAdmin: canAdmin(perms),
      permissions: perms,
    };
  }, [user]);
}
